import styled from "@emotion/styled";

import { TooltipVariant } from "components/ui/tooltip/Tooltip";

export const Kbd = styled("kbd", {
  shouldForwardProp: (prop) => prop !== "variant",
})<{ variant?: TooltipVariant }>(({ theme, variant = "default" }) => ({
  backgroundColor:
    variant === "default" ? "rgba(23, 31, 44, 0.5)" : theme.colors.sysBackgroundAlternative,
  color: variant === "default" ? theme.colors.sysTextWhite : theme.colors.sysTextDefault,
  padding: "0 4px",
  borderRadius: "4px",
  fontFamily: theme.font.fontFamily,

  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "20px",
  width: "fit-content",
  height: "20px",
}));

export const KbdGroup = styled.kbd({
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
});

export type KeyboardShortcutProps = React.ComponentProps<"kbd"> & {
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
  macOsRemapKeys?: boolean;
  keys: ("\\" | "↵")[]; // Add keys as needed as otherwise type will be massive
  variant?: TooltipVariant;
};

/**
 * @param ctrlKey Whether to show the Ctrl (or Control ⌃ on MacOS) key
 * @param metaKey Whether to show the Meta (Windows ⊞ or Command ⌘ on MacOS) key
 * @param altKey Whether to show the Alt (or Option ⌥ on MacOS) key
 * @param shiftKey Whether to show the Shift (or ⇧ on MacOS) key
 * @param macOsRemapKeys If true, remaps Ctrl to ⌘ Command and Meta to ⌥ Option on MacOS. Only affects display, not functionality.
 * @param keys An array of additional non-modifier keys to show
 * @param variant Whether to use light or default (dark) styling
 * @param ...rest Other props passed to the KbdGroup
 */
const KeyboardShortcut = ({
  ctrlKey,
  metaKey,
  altKey,
  shiftKey,
  macOsRemapKeys = false,
  keys,
  variant = "default",
  ...rest
}: KeyboardShortcutProps) => {
  const isMac = /mac/i.test(
    navigator.userAgentData ? navigator.userAgentData.platform : navigator.platform,
  );

  const ctrlKeyFinal = macOsRemapKeys && isMac ? altKey : ctrlKey;
  const metaKeyFinal = macOsRemapKeys && isMac ? ctrlKey : metaKey;

  return (
    <KbdGroup {...rest}>
      {shiftKey && (
        <Kbd variant={variant} aria-label="Shift">
          {isMac ? "⇧" : "Shift"}
        </Kbd>
      )}
      {ctrlKeyFinal && (
        <Kbd variant={variant} aria-label={isMac ? "Control" : "Ctrl"}>
          {isMac ? "⌃" : "Ctrl"}
        </Kbd>
      )}
      {metaKeyFinal && (
        <Kbd variant={variant} aria-label={isMac ? "Command" : "Win"}>
          {isMac ? "⌘" : "Win"}
        </Kbd>
      )}
      {altKey && (
        <Kbd variant={variant} aria-label={isMac ? "Option" : "Alt"}>
          {isMac ? "⌥" : "Alt"}
        </Kbd>
      )}
      {keys.map((key, index) => (
        <Kbd variant={variant} key={index}>
          {key}
        </Kbd>
      ))}
    </KbdGroup>
  );
};

export default KeyboardShortcut;
